import {
  Alert,
  AlertIcon,
  Center,
  Icon,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { FiFileText } from "react-icons/fi";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  CallOffMechanism,
  ContractStateDto,
  FrameworkAgreementDto,
  type FrameworkCallOffDto,
  useListFrameworkCallOffsQuery,
  Currency,
} from "../../autogen/bff-api";
import { EntityViewSkeleton } from "../../common/EntityViewSkeleton";
import { useApiError } from "../../common/errors/useApiError";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { urls } from "../../urls";
import { ContractStateTag } from "../contracts/edit/ContractStateTag";
import { ContractStatusTag } from "../../common/contracts/ContractStatusTag";
import { remainingUnderCeiling } from "./callOffAvailability";
import { NewCallOffModal } from "./NewCallOffModal";
import { PanelActionButton } from "./PanelActionButton";
import { HorizontalScrollBox } from "../../common/HorizontalScrollBox";
import { RowTitle } from "./RowTitle";
import { SectionCard } from "./SectionCard";
import { StartRefusalNote } from "./StartRefusalNote";
import { displaySum } from "../../common/formatting/displaySum";

const amount = (value: string | null | undefined) => (value == null ? undefined : Number(value));

export const CallOffsSection = ({ framework, canEdit }: { framework: FrameworkAgreementDto; canEdit: boolean }) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const [isStarting, setIsStarting] = useState(false);
  const { data, error, isLoading } = useListFrameworkCallOffsQuery({ frameworkAgreementId: framework.id });

  const showsMechanism = framework.callOffMechanism === CallOffMechanism.DirectAwardAndMiniCompetition;

  const offersCallOff = canEdit && framework.allowsDirectAward && framework.mayCreateCallOff;

  if (error) return displayer.show(error);
  if (isLoading || !data) return <EntityViewSkeleton />;

  const callOffs = data.callOffs ?? [];
  const currency = framework.ceilingValue?.currency ?? Currency.Nok;
  const total = amount(data.totalValue);
  const ceiling = amount(data.ceilingValue);
  const remaining = remainingUnderCeiling(framework);
  const spendWithheld = framework.ceilingValue?.value != null && framework.summary.remainingCeilingValue == null;
  const isOverCeiling =
    (remaining !== null && remaining < 0) || (total !== undefined && ceiling !== undefined && total > ceiling);

  return (
    <Stack spacing="3">
      {isOverCeiling && (
        <Alert status="warning" rounded="md">
          <AlertIcon />
          {t(
            "The call-offs made add up to more than the ceiling value of the framework agreement. No further call-offs can be made under it, and what exceeds the ceiling value must be handled, for example with a new competition.",
          )}
        </Alert>
      )}
      <SectionCard
        title={t("Call-offs")}
        notice={offersCallOff && <StartRefusalNote framework={framework} mt="2" />}
        action={
          offersCallOff && (
            <PanelActionButton
              framework={framework}
              label={t("New call-off")}
              onClick={() => setIsStarting(true)}
            />
          )
        }
      >
        {callOffs.length === 0 && spendWithheld ? (
          <CallOffsEmptyState message={t("You do not have access to the call-offs under this framework agreement.")} />
        ) : callOffs.length === 0 ? (
          <CallOffsEmptyState
            message={t("No call-offs have been made yet")}
            hint={t("A call-off is the contract that buys something under this framework agreement.")}
            action={
              offersCallOff && (
                <PanelActionButton
                  framework={framework}
                  label={t("New call-off")}
                  onClick={() => setIsStarting(true)}
                />
              )
            }
          />
        ) : (
          <>
            <HorizontalScrollBox>
              <CallOffsTable callOffs={callOffs} currency={currency} showsMechanism={showsMechanism} />
            </HorizontalScrollBox>
            <CallOffsCutOffNote shown={callOffs.length} total={data.totalCount} />
          </>
        )}
      </SectionCard>
      {isStarting && (
        <NewCallOffModal framework={framework} isOpen={isStarting} onClose={() => setIsStarting(false)} />
      )}
    </Stack>
  );
};

export const CallOffsTable = ({
  callOffs,
  currency,
  showsMechanism,
  showsSupplier = true,
}: {
  callOffs: FrameworkCallOffDto[];
  currency: Currency;
  showsMechanism: boolean;
  showsSupplier?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  return (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th>{t("Call-off")}</Th>
          {showsSupplier && <Th>{t("Supplier")}</Th>}
          <Th>{t("Status")}</Th>
          {showsMechanism && <Th>{t("Call-off mechanism")}</Th>}
          <Th isNumeric>{t("Value")}</Th>
          <Th>{t("Expires")}</Th>
        </Tr>
      </Thead>
      <Tbody>
        {callOffs.map((callOff) => (
          <Tr
            key={callOff.contractId}
            cursor="pointer"
            _hover={{ bg: "smBackgroundSecondary" }}
            onClick={() => navigate(urls.app.contracts.view.go(callOff.contractId))}
          >
            <Td fontWeight="semibold">
              <RowTitle>{callOff.title}</RowTitle>
            </Td>
            {showsSupplier && <Td>{callOff.supplierName ?? "-"}</Td>}
            <Td>
              <CallOffStatus callOff={callOff} />
            </Td>
            {showsMechanism && (
              <Td color="smMuted">{callOff.miniCompetitionId ? t("MiniCompetition") : t("DirectAward")}</Td>
            )}
            <Td isNumeric>
              {callOff.value
                ? displaySum({ value: Number(callOff.value), currency: callOff.currency ?? currency })
                : "-"}
            </Td>
            <Td>{callOff.endDate ? displayDate(callOff.endDate) : "-"}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};

export const CallOffsEmptyState = ({
  message,
  hint,
  action,
}: {
  message: string;
  hint?: ReactNode;
  action?: ReactNode;
}) => (
  <Center py="16" px="6" flexDirection="column" gap="3" color="smMuted">
    <Icon as={FiFileText} boxSize="28px" />
    <Text fontWeight="medium" color="smText">
      {message}
    </Text>
    {hint && (
      <Text fontSize="sm" maxW="420px" textAlign="center">
        {hint}
      </Text>
    )}
    {action}
  </Center>
);

export const CallOffsCutOffNote = ({ shown, total }: { shown: number; total: number }) => {
  const { t } = useTranslation();
  if (total <= shown) return null;
  return (
    <Text fontSize="sm" color="smMuted" px="6" py="3">
      {t("Showing the {{shown}} newest of {{count}} call-offs.", { shown, count: total })}
    </Text>
  );
};

const CallOffStatus = ({ callOff }: { callOff: FrameworkCallOffDto }) => {
  if (callOff.state === ContractStateDto.Draft) return <ContractStateTag state={ContractStateDto.Draft} size="sm" />;
  return callOff.status ? <ContractStatusTag status={callOff.status} size="sm" /> : <Text color="smMuted">-</Text>;
};
